import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { hrGetJob, hrJobApplications } from '../../lib/jobsApi';
import type { HrJob, HrApplicationRow } from '../../lib/jobsApi';
import { ApiError } from '../../lib/api';
import { formatRelativeDate } from '../../lib/formatRelativeDate';
import { stageLabel, viewedLabel } from './stageLabels';

/*
 * Velaigal /hr/jobs/:id/applicants — the per-job applicant review list.
 *
 * Loads the job (for the title header) and its applications in parallel.
 * Each row shows the candidate, their stage, an "n of N" task-attempt count,
 * and a screening chip per attempted task — the chip is status-only except
 * for `done`, so we never render a score unless the API actually sent one.
 * The full report (summary, criteria, red flags, re-screen) lives one level
 * down, on ApplicantDetail. Rows link through there, which also owns the
 * decision actions.
 *
 * A row's stage moves ONLY through POST /api/hr/applications/:id/decisions.
 * Opening a candidate records a view, never a decision, which is why "not
 * opened yet" is its own chip beside the stage rather than a stage of its own.
 */

const SCREENING_STATUS_LABEL: Record<string, string> = {
  queued: 'Screening queued',
  running: 'Screening running',
  done: 'Screened',
  failed: 'Screening failed',
  manual_review: 'Needs review',
};

const StageChip: React.FC<{ stage: string }> = ({ stage }) => {
  const isSaffron = stage === 'shortlisted' || stage === 'interviewing' || stage === 'hired';
  return (
    <span
      className="chip"
      style={isSaffron ? undefined : { backgroundColor: 'var(--color-hairline)', color: 'var(--color-ink-secondary)' }}
    >
      <span
        aria-hidden="true"
        className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
        style={{ backgroundColor: isSaffron ? 'var(--color-saffron-press)' : 'var(--color-ink-tertiary)' }}
      />
      {stageLabel(stage)}
    </span>
  );
};

const NotOpenedChip: React.FC<{ firstViewedAt: string | null }> = ({ firstViewedAt }) => {
  const label = viewedLabel(firstViewedAt);
  if (!label) return null;
  return (
    <span className="chip" style={{ backgroundColor: 'var(--color-hairline)', color: 'var(--color-ink-tertiary)' }}>
      {label}
    </span>
  );
};

const ScreeningChip: React.FC<{ screening: HrApplicationRow['screenings'][number] }> = ({ screening }) => {
  const label = SCREENING_STATUS_LABEL[screening.status] || screening.status;
  return (
    <span
      className="chip"
      style={{ backgroundColor: 'var(--color-hairline)', color: 'var(--color-ink-secondary)' }}
    >
      {label}
      {screening.status === 'done' && screening.score !== null && (
        <span className="text-ink-tertiary">· {screening.score}</span>
      )}
      {screening.linkAlive === false && (
        <span
          className="text-[11px]"
          style={{ color: 'var(--color-error)' }}
          title="Link was not reachable at screening time"
        >
          · link dead
        </span>
      )}
    </span>
  );
};

const LoadingNotice: React.FC = () => (
  <div role="status" className="max-w-3xl mx-auto text-center py-24">
    <span aria-hidden="true" className="velaigal-loader-rule mx-auto w-12 block" />
    <p className="mt-4 text-sm text-ink-tertiary">Loading applicants.</p>
  </div>
);

const NotFoundNotice: React.FC = () => (
  <div className="max-w-2xl mx-auto text-center py-16">
    <span className="label-editorial block mb-3">Not found</span>
    <h1 className="font-display text-3xl font-medium text-ink tracking-tight mb-4">
      This job isn&apos;t available.
    </h1>
    <p className="text-base text-ink-secondary leading-relaxed mb-8">
      It may have been deleted, or you don&apos;t have access to it.
    </p>
    <Link to="/hr/jobs" className="btn-primary">
      Back to your jobs
    </Link>
  </div>
);

const LoadErrorNotice: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="max-w-2xl mx-auto text-center py-16">
    <span className="label-editorial block mb-3" style={{ color: 'var(--color-error)' }}>
      Something went wrong
    </span>
    <h1 className="font-display text-3xl font-medium text-ink tracking-tight mb-4">
      Couldn&apos;t load applicants.
    </h1>
    <p className="text-base text-ink-secondary leading-relaxed mb-8">{message}</p>
    <button type="button" onClick={onRetry} className="btn-secondary">
      Try again
    </button>
  </div>
);

const EmptyNotice: React.FC = () => (
  <div className="bg-paper border border-hairline rounded-md p-8 text-center">
    <p className="text-ink">No applications yet — share the public link.</p>
  </div>
);

const ApplicantRow: React.FC<{ application: HrApplicationRow }> = ({ application }) => (
  <Link
    to={`/hr/applications/${application.id}`}
    className="card block no-underline text-ink"
  >
    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-3 flex-wrap mb-2">
          <StageChip stage={application.stage} />
          <NotOpenedChip firstViewedAt={application.firstViewedAt} />
          <span className="text-xs text-ink-tertiary">
            {application.tasksAttempted} of {application.taskCount} task{application.taskCount === 1 ? '' : 's'}
          </span>
        </div>
        <p className="font-display text-lg font-medium text-ink">{application.candidateName}</p>
        <p className="text-sm text-ink-secondary mt-0.5">{application.candidateEmail}</p>
      </div>
      <span className="text-xs text-ink-tertiary flex-shrink-0">
        Applied {formatRelativeDate(application.createdAt)}
      </span>
    </div>

    {application.screenings.length > 0 && (
      <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-hairline">
        {application.screenings.map((s) => (
          <ScreeningChip key={s.taskId} screening={s} />
        ))}
      </div>
    )}
  </Link>
);

const Applicants: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(true);
  const [loadNonce, setLoadNonce] = useState(0);
  const [loadError, setLoadError] = useState<{ message: string; notFound: boolean } | null>(null);
  const [job, setJob] = useState<HrJob | null>(null);
  const [applications, setApplications] = useState<HrApplicationRow[]>([]);

  // Cancelled-guarded so a fast unmount (or a stale in-flight retry) can't
  // setState afterward — mirrors the load-by-id effect in JobManage.tsx.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    (async () => {
      try {
        const [jobRes, appsRes] = await Promise.all([hrGetJob(id), hrJobApplications(id)]);
        if (cancelled) return;
        setJob(jobRes.job);
        setApplications(appsRes.applications);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) {
          setLoadError({ message: err.message, notFound: err.status === 404 });
        } else {
          setLoadError({ message: 'Check your connection and try again.', notFound: false });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, loadNonce]);

  if (loading) return <LoadingNotice />;
  if (loadError?.notFound) return <NotFoundNotice />;
  if (loadError) return <LoadErrorNotice message={loadError.message} onRetry={() => setLoadNonce((n) => n + 1)} />;
  if (!job) return null;

  return (
    <div className="max-w-3xl mx-auto">
      <span className="label-editorial block mb-3">Applicants</span>
      <h1 className="font-display text-3xl font-medium text-ink tracking-tight mb-8">{job.title}</h1>

      {applications.length === 0 ? (
        <EmptyNotice />
      ) : (
        <div className="space-y-4">
          {applications.map((application) => (
            <ApplicantRow key={application.id} application={application} />
          ))}
        </div>
      )}
    </div>
  );
};

export default Applicants;
